import { ImmortalDB as Database } from 'immortal-db'
import Session from '@/plugins/sdk/resources/Session'
import Profile from '@/plugins/sdk/resources/Profile'
import { CREATED, UPDATED } from '@/plugins/sdk/Events'
import Request, { POST, DELETE } from '@/plugins/sdk/Request'
import Vue from 'vue'

let instance

Session.on([ CREATED, UPDATED ], session => {
  instance = session
})

Profile.on(UPDATED, async function (profile) {
  if (await session.active && profile.id === instance.profile.id) {
    refresh()
    Vue.$pusher.subscribe(profile.id)
  }
})

const session = Object.freeze({
  get instance () {
    return Promise
      .resolve(instance || read())
  },
  get token () {
    return Promise
      .all([
        Database.get('session'),
        Database.get('secret'),
        Database.get('team')
      ])
      .then(segments => {
        return segments
          .filter(Boolean)
          .join('.')
      })
      .catch(() => {
        return undefined
      })
  },
  set token (value) {
    if (typeof value === 'string') {
      if (value.startsWith('Bearer ')) {
        value = value.substring(7, value.length)
      }

      const segments = value.split('.')

      Database.set('session', segments[0])
      Database.set('secret', segments[1])
      Database.set('team', segments[2])
    }
  },
  get expiry () {
    return Database.get('expiry', Date.now())
  },
  set expiry (value) {
    Database.set('expiry', new Date(value).valueOf())
  },
  get expired () {
    return Promise
      .resolve(this.expiry)
      .then(expiry => Date.now() >= expiry)
  },
  get active () {
    return Promise
      .resolve(this.expired)
      .then(expired => !expired)
  },
  get inactive () {
    return Promise
      .resolve(this.active)
      .then(active => !active)
  },
  establish,
  terminate,
  refresh,
  switchTeam,
  tradeTokenForSession
})

/**
 * Establish a new session.
 */
async function establish (email, password) {
  let success = false

  if (await session.active) {
    success = await refresh()
  } else {
    const request = Object.assign(new Request(), {
      method: POST,
      _pathname: 'authenticate',
      body: {
        email,
        password
      }
    })

    const response = await fetch(await request.build())

    success = (
      response.ok &&
      response.status === 201 &&
      response.headers.has('Authorization') &&
      response.headers.has('X-Session-Expires')
    )

    if (success) {
      session.token = response.headers.get('Authorization')
      session.expiry = response.headers.get('X-Session-Expires')

      Session.emit(CREATED, await read())
    }
  }

  return success
}

async function tradeTokenForSession (token) {
  const request = Object.assign(new Request(), {
    method: POST,
    _pathname: 'authenticate/trade-token',
    body: {
      token
    }
  })

  const response = await fetch(await request.build())
  let success = false

  success = (
    response.ok &&
    response.status === 201 &&
    response.headers.has('Authorization') &&
    response.headers.has('X-Session-Expires')
  )

  if (success) {
    session.token = response.headers.get('Authorization')
    session.expiry = response.headers.get('X-Session-Expires')

    Session.emit(CREATED, await read())
  }

  return success
}

/**
 * Refresh the session.
 */
async function refresh () {
  let success = false

  if (await session.active) {
    const request = Object.assign(new Request(), {
      method: POST,
      _pathname: 'session'
    })

    const response = await fetch(await request.build())

    success = (
      response.ok &&
      response.status === 204 &&
      response.headers.has('X-Session-Expires')
    )

    if (success) {
      session.expiry = response.headers.get('X-Session-Expires')
      Session.emit(UPDATED, await read())
    }
  }

  return success
}

/**
 * Terminate the session.
 */
async function terminate () {
  const request = Object.assign(new Request(), {
    method: DELETE,
    _pathname: 'session'
  })

  fetch(await request.build())

  await Promise.all([
    Database.remove('session'),
    Database.remove('secret'),
    Database.remove('team'),
    Database.remove('expiry')
  ])

  return true
}

/**
 * Reads the session.
 */
async function read () {
  return Session.read(undefined, request => {
    request.include = [
      'teams.account'
    ]
  })
}

/**
 * Switches the active team
 */
async function switchTeam (team) {
  await Database.set('team', team)
  return Database.get('team')
}

/**
 * Install the session handler
 */
export function install (Vue) {
  Object.defineProperty(Vue.prototype, '$session', { value: session })
}

export default session
